<script setup>
import { computed, ref } from 'vue';
import { debounce } from '@chatwoot/utils';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { buildConversationStatusBadge } from 'dashboard/helper/conversationStatusBadge';
import liveReports from 'dashboard/api/liveReports';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'shared/components/Spinner.vue';

const AGENT_PAGE_SIZE = 10;

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const dialogRef = ref(null);
const title = ref('');
const description = ref('');
const kind = ref('agents');
const source = ref('live');
const agents = ref([]);
const params = ref({});
const tabs = ref([]);
const search = ref('');
const conversations = ref([]);
const page = ref(1);
const perPage = ref(AGENT_PAGE_SIZE);
const total = ref(0);
const isLoading = ref(false);
const hasError = ref(false);
const generation = ref(0);

const activeTabIndex = computed(() =>
  Math.max(
    tabs.value.findIndex(tab => tab.key === params.value.metric),
    0
  )
);
const isEmpty = computed(() => {
  if (isLoading.value || hasError.value) return false;
  return total.value === 0;
});
const showPagination = computed(
  () => !isLoading.value && !hasError.value && total.value > perPage.value
);
const filteredAgents = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return agents.value;
  return agents.value.filter(agent =>
    [agent.name, agent.email].some(value => value?.toLowerCase().includes(term))
  );
});
const pagedAgents = computed(() =>
  filteredAgents.value.slice(
    (page.value - 1) * perPage.value,
    page.value * perPage.value
  )
);

const statusLabel = status => {
  if (status === 'online') return t('OVERVIEW_REPORTS.AGENT_STATUS.ONLINE');
  if (status === 'busy') return t('OVERVIEW_REPORTS.AGENT_STATUS.BUSY');
  if (status === 'offline') return t('OVERVIEW_REPORTS.AGENT_STATUS.OFFLINE');
  return status;
};

const conversationLink = id =>
  frontendURL(`accounts/${accountId.value}/conversations/${id}`);

const conversationBadge = conversation =>
  buildConversationStatusBadge(t, {
    status: conversation.status,
    assigneeId: conversation.assignee_id,
  });

const cleanParams = raw =>
  Object.fromEntries(
    Object.entries(raw).filter(
      ([, value]) => value !== null && value !== undefined && value !== ''
    )
  );

const fetchConversations = async nextPage => {
  generation.value += 1;
  const current = generation.value;
  isLoading.value = true;
  hasError.value = false;
  try {
    const query = cleanParams({
      ...params.value,
      page: nextPage,
      q: search.value.trim(),
    });
    const request =
      source.value === 'heatmap'
        ? liveReports.getHeatmapMetricItems(query)
        : liveReports.getConversationMetricItems(query);
    const { data } = await request;
    if (current !== generation.value) return;

    total.value = data.meta.count;
    page.value = data.meta.page;
    perPage.value = data.meta.per_page;
    conversations.value = data.payload;
  } catch {
    if (current === generation.value) hasError.value = true;
  } finally {
    if (current === generation.value) isLoading.value = false;
  }
};

const open = payload => {
  title.value = payload.title;
  description.value = payload.description || '';
  kind.value = payload.kind;
  source.value = payload.source || 'live';
  agents.value = payload.agents || [];
  params.value = payload.params || {};
  tabs.value = payload.tabs || [];
  search.value = '';
  conversations.value = [];
  page.value = 1;
  perPage.value = AGENT_PAGE_SIZE;
  total.value = agents.value.length;
  hasError.value = false;
  dialogRef.value?.open();

  if (kind.value === 'agents') {
    generation.value += 1;
    isLoading.value = false;
    return;
  }
  fetchConversations(1);
};

const searchConversations = debounce(() => fetchConversations(1), 400);

const onSearch = () => {
  if (kind.value === 'agents') {
    page.value = 1;
    total.value = filteredAgents.value.length;
    return;
  }
  searchConversations();
};

const changePage = nextPage => {
  if (kind.value === 'agents') {
    page.value = nextPage;
    return;
  }
  fetchConversations(nextPage);
};

const changeTab = tab => {
  params.value = { ...params.value, metric: tab.key };
  fetchConversations(1);
};

const close = () => dialogRef.value?.close();

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
    overflow-y-auto
  >
    <template #title>
      {{ title }}
    </template>
    <template #description>
      <p v-if="description" class="mb-0 text-sm text-n-slate-11">
        {{ description }}
      </p>
    </template>

    <TabBar
      v-if="tabs.length"
      :tabs="tabs"
      :initial-active-tab="activeTabIndex"
      class="self-start"
      @tab-changed="changeTab"
    />
    <Input
      v-model="search"
      type="search"
      :placeholder="
        kind === 'agents'
          ? t('OVERVIEW_REPORTS.DETAIL.SEARCH_AGENTS')
          : t('OVERVIEW_REPORTS.DETAIL.SEARCH_CONVERSATIONS')
      "
      @update:model-value="onSearch"
    />
    <div class="flex flex-col gap-3 max-h-[60vh] overflow-y-auto">
      <div
        v-if="isLoading"
        class="flex items-center justify-center gap-2 py-8 text-n-slate-11"
      >
        <Spinner />
        <span>{{ t('GENERAL.LOADING') }}</span>
      </div>
      <p v-else-if="hasError" class="mb-0 text-sm text-n-ruby-11">
        {{ t('OVERVIEW_REPORTS.DETAIL.ERROR') }}
      </p>
      <p v-else-if="isEmpty" class="mb-0 text-sm text-n-slate-11">
        {{
          kind === 'agents'
            ? t('OVERVIEW_REPORTS.DETAIL.EMPTY_AGENTS')
            : t('OVERVIEW_REPORTS.DETAIL.EMPTY_CONVERSATIONS')
        }}
      </p>

      <template v-else-if="kind === 'agents'">
        <CardLayout
          v-for="agent in pagedAgents"
          :key="agent.id"
          layout="row"
          class="[&>div]:px-4 [&>div]:py-3"
        >
          <Avatar
            :src="agent.thumbnail"
            :name="agent.name"
            :status="agent.status"
            :size="32"
            hide-offline-status
            rounded-full
          />
          <div class="flex flex-col min-w-0 flex-1">
            <span class="text-sm text-n-slate-12 truncate">
              {{ agent.name }}
            </span>
            <span class="text-xs text-n-slate-11 truncate">
              {{ agent.email }}
            </span>
          </div>
          <span
            class="shrink-0 rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-12"
          >
            {{ statusLabel(agent.status) }}
          </span>
        </CardLayout>
      </template>

      <template v-else>
        <router-link
          v-for="conversation in conversations"
          :key="conversation.id"
          :to="conversationLink(conversation.id)"
          @click="close"
        >
          <CardLayout
            class="[&>div]:items-start [&>div]:px-4 [&>div]:py-3 hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
          >
            <div class="flex w-full items-center justify-between gap-3">
              <div class="flex min-w-0 items-center gap-2">
                <span class="flex items-center gap-1 text-sm text-n-slate-12">
                  <Icon icon="i-lucide-hash" class="size-4 text-n-slate-11" />
                  {{ conversation.id }}
                </span>
                <span
                  v-if="conversationBadge(conversation)"
                  class="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="conversationBadge(conversation).class"
                >
                  {{ conversationBadge(conversation).label }}
                </span>
              </div>
              <span
                v-if="conversation.last_activity_at"
                class="shrink-0 text-xs text-n-slate-11"
              >
                {{ dynamicTime(conversation.last_activity_at) }}
              </span>
            </div>
            <p class="mb-0 text-sm text-n-slate-12 truncate">
              {{
                conversation.contact_name ||
                t('OVERVIEW_REPORTS.DETAIL.CONTACT')
              }}
            </p>
            <p
              class="mb-0 flex items-center gap-2 text-xs text-n-slate-11 truncate"
            >
              <span v-if="conversation.inbox_name" class="truncate">
                {{ conversation.inbox_name }}
              </span>
              <span
                v-if="conversation.inbox_name"
                class="h-3 w-px shrink-0 bg-n-strong"
              />
              <span class="truncate">
                {{
                  conversation.assignee_name ||
                  t('OVERVIEW_REPORTS.DETAIL.UNASSIGNED')
                }}
              </span>
            </p>
          </CardLayout>
        </router-link>
      </template>
    </div>

    <template #footer>
      <div class="flex w-full flex-col gap-3">
        <PaginationFooter
          v-if="showPagination"
          class="before:hidden"
          :current-page="page"
          :total-items="total"
          :items-per-page="perPage"
          current-page-info="OVERVIEW_REPORTS.DETAIL.SHOWING"
          @update:current-page="changePage"
        />
        <div class="flex justify-end w-full">
          <Button
            variant="faded"
            color="slate"
            type="button"
            :label="t('GENERAL.CLOSE')"
            @click="close"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
